import { StockLevelResponse } from '../types/stock';

/** One warehouse row as the panel renders it. */
export interface StockLevelRow {
  id: string;
  /** Lagerkürzel (LAGERSTAMM.LAGER). */
  key: string;
  /** Lagername without the Kürzel the backend prefixes it with. */
  name: string;
  amount: number;
  /** Dispostand — null unless the query asked for the `disposition` flag. */
  disposition: number | null;
}

/** Drops the Kürzel that the stock backend prefixes onto the name ("010" +
    "010 LGM Gleinstätten" rendered as "010 010 LGM Gleinstätten").

    Only a WHITESPACE-separated prefix with something left over is dropped, so
    a name that merely starts with the same characters survives intact:
    "999"/"999" keeps "999" (stripping blanked it) and "999"/"999-Zentrale"
    keeps the full name (stripping left "-Zentrale"). */
export function withoutKeyPrefix(
  key: string | null | undefined,
  name: string | null | undefined
): string {
  const k = key?.trim() ?? '';
  const n = name?.trim() ?? '';
  if (!k || !n.startsWith(k)) return n;
  const rest = n.slice(k.length);
  return /^\s/.test(rest) && rest.trim() ? rest.trim() : n;
}

/** Display rows for "Lagerstand je Filiale". The endpoint does not guarantee an
    order, and `stock.name` repeats the Kürzel ("010" + "010 LGM Gleinstätten"),
    which rendered as "010 010 LGM Gleinstätten". */
export function toStockLevelRows(levels: StockLevelResponse[]): StockLevelRow[] {
  return levels
    .map((row) => {
      const key = row.stock?.key?.trim() ?? '';
      return {
        id: row.stock?.id ?? key,
        key,
        name: withoutKeyPrefix(key, row.stock?.name),
        amount: row.stockLevel ?? 0,
        disposition: row.dispositionQuantity ?? null,
      };
    })
    .sort((a, b) => a.key.localeCompare(b.key, 'de', { numeric: true }));
}
